/**
 * Rasterizes the already-rendered <BusinessCardPreview> SVG (front or back)
 * into an RGB JPEG Blob, for upload to the print-order backend.
 *
 * BusinessCardPreview is a pure client-side component -- its FitText
 * auto-shrink measurement uses SVGTextElement.getBBox(), a browser-only API
 * -- so there is no server-side renderer to reuse. This instead rasterizes
 * the live, already-measured SVG the browser has already laid out, rather
 * than duplicating that measurement logic in Node.
 */
export async function exportCardImage(side: 'front' | 'back'): Promise<Blob> {
  const svgEl = document.querySelector<SVGSVGElement>(`svg[aria-label="Review card ${side}"]`);
  if (!svgEl) throw new Error(`Review card ${side} is not currently rendered.`);

  const clone = svgEl.cloneNode(true) as SVGSVGElement;

  // Every <image> needs inlining as a data: URI before serializing, not
  // just the genuinely cross-origin ones (the logo, from R2, and the QR
  // code, from the API host) -- found live 2026-09-17: the bundled
  // front/back template PNGs are same-origin, but larger ones (front-fixed,
  // the Google G icon, the stars row) are above webpack's inline threshold
  // and get emitted as separate files referenced by a root-relative path
  // (e.g. /static/media/xyz.png). That path resolves fine while the SVG is
  // live in the page, but once serialized into a standalone `blob:` document
  // below, it has no reliable base to resolve against and silently fails to
  // load -- rendering those elements as nothing rather than throwing. The
  // Google wordmark happened to render because it's small enough that
  // webpack had already inlined it as a data: URI at build time, which is
  // what every image here needs, one way or another.
  const images = Array.from(clone.querySelectorAll('image'));
  await Promise.all(images.map(async (img) => {
    const href = img.getAttribute('href');
    if (!href || href.startsWith('data:')) return;
    // Resolved against the live document's base -- not the eventual blob:
    // URL, which has no meaningful base of its own -- so both a root-
    // relative bundled-asset path and an already-absolute cross-origin URL
    // end up fetched from the right place.
    const absoluteHref = new URL(href, document.baseURI).href;
    let res: Response;
    try {
      // 'reload' bypasses the browser's own HTTP cache on the way in and
      // refreshes it on the way out. Needed because these assets (logos,
      // QR codes) are served with a 1-year `immutable` Cache-Control --
      // exactly right for normal <img> display, since a changed file gets a
      // new hashed URL, but it also means any browser that ever cached this
      // exact URL before a CORS/CSP fix landed keeps serving that stale,
      // header-less response forever, with no way to know the origin now
      // answers correctly. Found live 2026-09-16: curl always looked fine
      // (no persistent cache), the browser kept failing regardless.
      res = await fetch(absoluteHref, { cache: 'reload' });
    } catch (fetchErr) {
      // The one failure with no HTTP status to inspect -- a CORS or CSP
      // block surfaces here as a generic "Failed to fetch", so the URL is
      // the only diagnostic left; re-thrown with it attached rather than
      // losing it to a shared catch block upstream.
      throw new Error(`Could not fetch ${absoluteHref} (${(fetchErr as Error).message}).`);
    }
    if (!res.ok) throw new Error(`Fetching ${absoluteHref} returned ${res.status}.`);
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error(`Failed to inline an image for the ${side} of the card.`));
      reader.readAsDataURL(blob);
    });
    img.setAttribute('href', dataUrl);
  }));

  // The SVG's viewBox units ARE 300 DPI pixels (BusinessCardPreview's own
  // convention), so the canvas needs no separate scale factor.
  const width = svgEl.viewBox.baseVal.width || svgEl.clientWidth;
  const height = svgEl.viewBox.baseVal.height || svgEl.clientHeight;

  const svgString = new XMLSerializer().serializeToString(clone);
  const svgUrl = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' }));

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`Failed to rasterize the ${side} of the card.`));
      el.src = svgUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not supported in this browser.');
    // JPEG has no alpha channel -- painting white first is a cheap guard
    // against any edge pixel exporting as black instead of the card's own
    // background once flattened.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error(`Failed to export the ${side} of the card.`));
      }, 'image/jpeg', 0.92);
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}
